// src/pages/DivergenciasFarma.js — Gestão de divergências (layout reformulado)
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, TextField, MenuItem, FormControl,
  InputLabel, Select, Chip, IconButton, CircularProgress, Alert,
  Divider, Stack, Avatar, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, List, ListItem, ListItemText
} from '@mui/material';
import {
  ReportProblem as WarningIcon, Refresh as RefreshIcon,
  Build as BuildIcon, Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon, Cancel as CancelIcon,
  Add as AddIcon, List as ListIcon,
  ErrorOutline as PendenteIcon, WarningAmber as CriticaIcon,
  HighlightOff as RejeitadaIcon, TaskAlt as ResolvidaIcon,
  CalendarMonth as CalendarIcon, Person as PersonIcon,
  Close as CloseIcon, ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon,
  RotateLeft as RotateLeftIcon, RotateRight as RotateRightIcon,
  Fullscreen as FullscreenIcon, ZoomOutMap as ZoomResetIcon,
  Image as ImageIcon, History as HistoryIcon, ExpandLess, ExpandMore,
  Edit as EditIcon, AttachFile as AttachFileIcon, Star as StarIcon,
  CheckCircleOutline as CheckOutlineIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const STATUS_CFG = {
  pendente:   { label: 'Pendente',   bg: '#fef3c7', fg: '#92400e' },
  divergente: { label: 'Divergente', bg: '#fee2e2', fg: '#991b1b' },
  resolvida:  { label: 'Resolvida',  bg: '#dbeafe', fg: '#1e40af' },
  aprovada:   { label: 'Aprovada',   bg: '#d1fae5', fg: '#065f46' },
  rejeitada:  { label: 'Rejeitada',  bg: '#fed7aa', fg: '#9a3412' },
  em_revisao: { label: 'Em Revisão', bg: '#e5e7eb', fg: '#374151' }
};

const PRIORIDADE_CFG = {
  critica: { label: 'Crítica', bg: '#fecaca', fg: '#991b1b' },
  alta:    { label: 'Alta',    bg: '#fed7aa', fg: '#9a3412' },
  media:   { label: 'Média',   bg: '#fde68a', fg: '#854d0e' },
  baixa:   { label: 'Baixa',   bg: '#e5e7eb', fg: '#374151' }
};

const formatarData = (d) => {
  if (!d) return '—';
  // O driver agora entrega a data ja no fuso local (useUTC:false em
  // config/sqlserver.js), entao o parse direto basta. Antes daqui havia um
  // remendo que arrancava o 'Z' da string — mantido ate 19/08/2026, quando a
  // causa foi corrigida na conexao. Sem remover, esta tela passaria a mostrar
  // 3h A MAIS.
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  const dia = String(dt.getDate()).padStart(2, '0');
  const mes = String(dt.getMonth() + 1).padStart(2, '0');
  const ano = dt.getFullYear();
  const hh = String(dt.getHours()).padStart(2, '0');
  const mm = String(dt.getMinutes()).padStart(2, '0');
  return `${dia}/${mes}/${ano}, ${hh}:${mm}`;
};

const DivergenciasFarma = () => {
  const { api } = useAuth();

  const [filiais, setFiliais] = useState([]);
  const [divergencias, setDivergencias] = useState([]);
  const [contagem, setContagem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [filtros, setFiltros] = useState({
    filial_id: '',
    status: 'divergente',
    prioridade: ''
  });

  // Modais
  const [detalheAberto, setDetalheAberto] = useState(false);
  const [divSelecionada, setDivSelecionada] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [historicoReceita, setHistoricoReceita] = useState([]);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);
  const [histOpen, setHistOpen] = useState(true);

  // Imagem da receita (buscada via conferencia-farma)
  const [imagensReceita, setImagensReceita] = useState([]);
  const [imgIdx, setImgIdx] = useState(0);
  const [imgScale, setImgScale] = useState(1);
  const [imgRotation, setImgRotation] = useState(0);

  const [acaoModal, setAcaoModal] = useState(null);
  const [comentario, setComentario] = useState('');
  const [valorCorrigido, setValorCorrigido] = useState('');
  const [salvando, setSalvando] = useState(false);

  const [novaDivergencia, setNovaDivergencia] = useState({
    numero_requisicao: '', filial_id: '', tipo_divergencia: '',
    descricao_divergencia: '', prioridade: 'media', valor_original: ''
  });

  const carregarFiliais = useCallback(async () => {
    try {
      const resp = await api.listarFiliais();
      if (resp.success) setFiliais(resp.dados || []);
    } catch (e) { console.error(e); }
  }, [api]);

  const carregarDivergencias = useCallback(async () => {
    if (!filtros.filial_id) { setDivergencias([]); setContagem(null); return; }
    setLoading(true);
    setError('');
    try {
      const [list, count] = await Promise.all([
        api.divergenciasListar(filtros.filial_id, {
          status: filtros.status, prioridade: filtros.prioridade, limit: 200
        }),
        api.divergenciasCount(filtros.filial_id)
      ]);
      if (list.success) setDivergencias(list.dados?.divergencias || []);
      if (count.success) setContagem(count.dados);
    } catch (e) {
      setError(e.message || 'Erro ao carregar divergências');
    } finally {
      setLoading(false);
    }
  }, [api, filtros]);

  useEffect(() => { carregarFiliais(); }, [carregarFiliais]);
  useEffect(() => { carregarDivergencias(); }, [carregarDivergencias]);

  const abrirDetalhe = async (div) => {
    setDivSelecionada(div);
    setDetalheAberto(true);
    setHistorico([]);
    setHistoricoReceita([]);
    setImagensReceita([]);
    setImgIdx(0); setImgScale(1); setImgRotation(0);
    setComentario('');
    setCarregandoDetalhe(true);
    try {
      const [detResp, imgResp, histRecResp] = await Promise.all([
        api.divergenciaDetalhes(div.id),
        api.conferenciaFarmaImagens(div.numero_requisicao, div.filial_id),
        api.divergenciasHistoricoReceita(div.numero_requisicao, div.filial_id)
      ]);
      if (detResp.success) {
        setDivSelecionada(detResp.dados);
        setHistorico(detResp.dados.historico || []);
      }
      if (imgResp.success) setImagensReceita(imgResp.dados?.imagens || []);
      if (histRecResp.success) setHistoricoReceita(histRecResp.dados || []);
    } catch (e) { console.error(e); }
    finally { setCarregandoDetalhe(false); }
  };

  const fecharDetalhe = () => {
    setDetalheAberto(false);
    setDivSelecionada(null);
    setHistorico([]);
    setHistoricoReceita([]);
    setImagensReceita([]);
    setComentario('');
    setError('');
  };

  const imgAtual = imagensReceita[imgIdx] || null;
  const urlAtual = imgAtual?.signed_url || imgAtual?.url_imagem;

  const proximaImg = () => {
    setImgScale(1); setImgRotation(0);
    setImgIdx(i => (i + 1) % imagensReceita.length);
  };
  const anteriorImg = () => {
    setImgScale(1); setImgRotation(0);
    setImgIdx(i => (i - 1 + imagensReceita.length) % imagensReceita.length);
  };

  const abrirAcao = (acao, div = null) => {
    if (div) setDivSelecionada(div);
    setAcaoModal(acao);
    setComentario('');
    setValorCorrigido('');
  };

  const fecharAcao = () => {
    setAcaoModal(null);
    setComentario('');
    setValorCorrigido('');
  };

  // Resolver inline (do modal principal): usa o `comentario` do form
  const resolverInline = async () => {
    if (!divSelecionada) return;
    if (comentario.trim().length < 10) {
      setError('Comentário precisa ter no mínimo 10 caracteres');
      return;
    }
    setSalvando(true);
    setError(''); setSuccess('');
    try {
      const resp = await api.divergenciaResolver(divSelecionada.id, {
        comentario_resolucao: comentario.trim(),
        valor_corrigido: null
      });
      if (resp?.success) {
        setSuccess('Divergência resolvida');
        fecharDetalhe();
        await carregarDivergencias();
      } else {
        setError(resp?.message || 'Erro ao resolver');
      }
    } catch (e) {
      setError(e.message || 'Erro de conexão');
    } finally {
      setSalvando(false);
    }
  };

  const aprovarInline = async () => {
    if (!divSelecionada) return;
    setSalvando(true);
    setError(''); setSuccess('');
    try {
      const resp = await api.divergenciaAprovar(divSelecionada.id, {
        comentario_aprovacao: comentario.trim() || null
      });
      if (resp?.success) {
        setSuccess('Divergência aprovada');
        fecharDetalhe();
        await carregarDivergencias();
      } else {
        setError(resp?.message || 'Erro ao aprovar');
      }
    } catch (e) {
      setError(e.message || 'Erro de conexão');
    } finally {
      setSalvando(false);
    }
  };

  const rejeitarInline = async () => {
    if (!divSelecionada) return;
    if (comentario.trim().length < 10) {
      setError('Comentário de rejeição precisa ter no mínimo 10 caracteres');
      return;
    }
    setSalvando(true);
    setError(''); setSuccess('');
    try {
      const resp = await api.divergenciaRejeitar(divSelecionada.id, {
        comentario_aprovacao: comentario.trim()
      });
      if (resp?.success) {
        setSuccess('Divergência rejeitada');
        fecharDetalhe();
        await carregarDivergencias();
      } else {
        setError(resp?.message || 'Erro ao rejeitar');
      }
    } catch (e) {
      setError(e.message || 'Erro de conexão');
    } finally {
      setSalvando(false);
    }
  };

  const executarAcao = async () => {
    if (!divSelecionada || !acaoModal) return;
    if (['resolver', 'rejeitar'].includes(acaoModal) && comentario.trim().length < 10) {
      setError('Comentário precisa ter no mínimo 10 caracteres');
      return;
    }
    setSalvando(true);
    setError(''); setSuccess('');
    try {
      let resp;
      if (acaoModal === 'resolver') {
        resp = await api.divergenciaResolver(divSelecionada.id, {
          comentario_resolucao: comentario,
          valor_corrigido: valorCorrigido || null
        });
      } else if (acaoModal === 'aprovar') {
        resp = await api.divergenciaAprovar(divSelecionada.id, { comentario_aprovacao: comentario });
      } else if (acaoModal === 'rejeitar') {
        resp = await api.divergenciaRejeitar(divSelecionada.id, { comentario_aprovacao: comentario });
      }
      if (resp?.success) {
        setSuccess(resp.message || 'Ação realizada');
        fecharAcao();
        fecharDetalhe();
        await carregarDivergencias();
      } else {
        setError(resp?.message || 'Erro na operação');
      }
    } catch (e) {
      setError(e.message || 'Erro de conexão');
    } finally {
      setSalvando(false);
    }
  };

  const abrirCriar = () => {
    setNovaDivergencia({
      numero_requisicao: '', filial_id: filtros.filial_id || '',
      tipo_divergencia: '', descricao_divergencia: '',
      prioridade: 'media', valor_original: ''
    });
    setAcaoModal('criar');
  };

  const criarDivergencia = async () => {
    const { numero_requisicao, filial_id, descricao_divergencia } = novaDivergencia;
    if (!numero_requisicao || !filial_id || !descricao_divergencia) {
      setError('Preencha NRRQU, filial e descrição'); return;
    }
    if (descricao_divergencia.trim().length < 10) {
      setError('Descrição precisa ter no mínimo 10 caracteres'); return;
    }
    setSalvando(true);
    setError(''); setSuccess('');
    try {
      const resp = await api.divergenciaCriar(novaDivergencia);
      if (resp.success) {
        setSuccess('Divergência registrada');
        fecharAcao();
        await carregarDivergencias();
      } else {
        setError(resp.message || 'Erro ao criar');
      }
    } catch (e) {
      setError(e.message || 'Erro de conexão');
    } finally {
      setSalvando(false);
    }
  };

  const renderChip = (cfg, text) => (
    <Chip
      size="small"
      label={text || cfg.label}
      sx={{ bgcolor: cfg.bg, color: cfg.fg, fontWeight: 'bold', height: 22 }}
    />
  );

  const StatCard = ({ icon, iconBg, iconColor, label, sub, value }) => (
    <Paper sx={{ p: 2.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 200 }}>
      <Avatar sx={{ bgcolor: iconBg, width: 44, height: 44 }}>
        {React.cloneElement(icon, { sx: { color: iconColor } })}
      </Avatar>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" fontWeight="bold">{label}</Typography>
        <Typography variant="caption" color="text.secondary" display="block">{sub}</Typography>
      </Box>
      <Typography variant="h4" fontWeight="bold" color="text.secondary">
        {value ?? 0}
      </Typography>
    </Paper>
  );

  return (
    <Box>
      {/* Header */}
      <Paper
        sx={{
          p: 3, mb: 2, borderRadius: 3, display: 'flex',
          alignItems: 'center', justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <WarningIcon sx={{ color: '#ef4444', fontSize: 36, mr: 1.5 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">Divergências</Typography>
            <Typography variant="body2" color="text.secondary">
              Resolução de divergências identificadas na conferência
            </Typography>
          </Box>
        </Box>
        <Avatar sx={{ bgcolor: '#fee2e2', width: 56, height: 56 }}>
          <WarningIcon sx={{ color: '#ef4444', fontSize: 30 }} />
        </Avatar>
      </Paper>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

      {/* 4 Stat cards */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <StatCard
          icon={<PendenteIcon />} iconBg="#fee2e2" iconColor="#ef4444"
          label="Pendentes" sub="Aguardando resolução"
          value={contagem?.total_pendentes}
        />
        <StatCard
          icon={<CriticaIcon />} iconBg="#fef3c7" iconColor="#f59e0b"
          label="Críticas" sub="Prioridade alta"
          value={contagem?.criticas}
        />
        <StatCard
          icon={<RejeitadaIcon />} iconBg="#ffedd5" iconColor="#f97316"
          label="Rejeitadas" sub="Requerem nova resolução"
          value={contagem?.rejeitadas}
        />
        <StatCard
          icon={<ResolvidaIcon />} iconBg="#d1fae5" iconColor="#10b981"
          label="Resolvidas" sub="Aguardando aprovação"
          value={contagem?.resolvidas}
        />
      </Stack>

      {/* Filtros */}
      <Paper sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>Filial *</InputLabel>
              <Select
                label="Filial *"
                value={filtros.filial_id}
                onChange={(e) => setFiltros(p => ({ ...p, filial_id: e.target.value }))}
                displayEmpty notched
              >
                <MenuItem value=""><em>Selecione uma filial</em></MenuItem>
                {filiais.map(f => (
                  <MenuItem key={f.id} value={f.codigo ?? f.id}>{f.nome_filial}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>Status</InputLabel>
              <Select
                label="Status" notched
                value={filtros.status}
                onChange={(e) => setFiltros(p => ({ ...p, status: e.target.value }))}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="divergente">Divergentes</MenuItem>
                <MenuItem value="pendente">Pendentes</MenuItem>
                <MenuItem value="resolvida">Resolvidas</MenuItem>
                <MenuItem value="aprovada">Aprovadas</MenuItem>
                <MenuItem value="rejeitada">Rejeitadas</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>Prioridade</InputLabel>
              <Select
                label="Prioridade" notched displayEmpty
                value={filtros.prioridade}
                onChange={(e) => setFiltros(p => ({ ...p, prioridade: e.target.value }))}
              >
                <MenuItem value="">Todas as prioridades</MenuItem>
                <MenuItem value="critica">Crítica</MenuItem>
                <MenuItem value="alta">Alta</MenuItem>
                <MenuItem value="media">Média</MenuItem>
                <MenuItem value="baixa">Baixa</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth variant="contained"
                startIcon={<RefreshIcon />}
                onClick={carregarDivergencias}
                sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
              >
                Atualizar
              </Button>
              <Button
                variant="outlined" color="error" sx={{ minWidth: 0, px: 1 }}
                onClick={abrirCriar}
                disabled={!filtros.filial_id}
                title="Nova divergência"
              >
                <AddIcon />
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Lista */}
      <Paper sx={{ p: 2.5, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ListIcon sx={{ color: '#64748b', mr: 1 }} />
          <Typography variant="h6" fontWeight="bold">Lista de Divergências</Typography>
        </Box>

        {!filtros.filial_id ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Selecione uma filial para visualizar as divergências.
            </Typography>
          </Box>
        ) : loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : divergencias.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Nenhuma divergência encontrada.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {divergencias.map(d => {
              const statusCfg = STATUS_CFG[d.status] || { label: d.status, bg: '#e5e7eb', fg: '#374151' };
              const prioCfg = PRIORIDADE_CFG[d.prioridade] || { label: d.prioridade, bg: '#e5e7eb', fg: '#374151' };

              return (
                <Paper
                  key={d.id}
                  variant="outlined"
                  sx={{ p: 2.5, borderRadius: 2 }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={10}>
                      {/* Linha 1: #ID status prioridade nrrqu */}
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="body1" fontWeight="bold" color="text.primary">
                          #{d.id}
                        </Typography>
                        {renderChip(statusCfg)}
                        {renderChip(prioCfg)}
                        <Typography variant="body2" color="text.secondary">
                          {d.numero_requisicao}
                        </Typography>
                      </Stack>

                      {/* Título / tipo */}
                      {d.tipo_divergencia && (
                        <Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
                          {d.tipo_divergencia}
                        </Typography>
                      )}

                      {/* Descrição */}
                      <Typography variant="body2" sx={{
                        color: 'text.secondary', mb: 1,
                        display: '-webkit-box', WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical', overflow: 'hidden'
                      }}>
                        {d.descricao_divergencia || '—'}
                      </Typography>

                      {/* Meta */}
                      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CalendarIcon fontSize="small" sx={{ color: '#64748b', mr: 0.5 }} />
                          <Typography variant="caption" color="text.secondary">
                            {formatarData(d.created_at)}
                          </Typography>
                        </Box>
                        {d.criado_por_usuario && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon fontSize="small" sx={{ color: '#64748b', mr: 0.5 }} />
                            <Typography variant="caption" color="text.secondary">
                              {d.criado_por_usuario}
                            </Typography>
                          </Box>
                        )}
                        {d.resolved_at && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CheckCircleIcon fontSize="small" sx={{ color: '#10b981', mr: 0.5 }} />
                            <Typography variant="caption" color="text.secondary">
                              Resolvida em {formatarData(d.resolved_at)}
                            </Typography>
                          </Box>
                        )}
                      </Stack>

                      {/* Box de resolução (se houver) */}
                      {d.comentario_resolucao && (
                        <Paper
                          variant="outlined"
                          sx={{
                            mt: 1.5, p: 1.5, borderLeft: '4px solid #10b981',
                            bgcolor: '#f0fdf4'
                          }}
                        >
                          <Typography variant="body2" fontWeight="bold" sx={{ color: '#065f46', mb: 0.5 }}>
                            Resolução:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#064e3b', mb: 0.5 }}>
                            {d.comentario_resolucao}
                          </Typography>
                          {d.resolvido_por_usuario && (
                            <Typography variant="caption" sx={{ color: '#047857' }}>
                              Resolvido por: {d.resolvido_por_usuario}
                            </Typography>
                          )}
                        </Paper>
                      )}

                      {/* Box de aprovação / rejeição */}
                      {d.comentario_aprovacao && (
                        <Paper
                          variant="outlined"
                          sx={{
                            mt: 1, p: 1.5,
                            borderLeft: `4px solid ${d.status === 'aprovada' ? '#10b981' : '#f59e0b'}`,
                            bgcolor: d.status === 'aprovada' ? '#f0fdf4' : '#fef3c7'
                          }}
                        >
                          <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                            {d.status === 'aprovada' ? 'Aprovação' : 'Rejeição'}:
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {d.comentario_aprovacao}
                          </Typography>
                          {d.aprovado_por_usuario && (
                            <Typography variant="caption">
                              Por: {d.aprovado_por_usuario}
                            </Typography>
                          )}
                        </Paper>
                      )}
                    </Grid>

                    {/* Ações */}
                    <Grid item xs={12} md={2}>
                      <Stack spacing={1}>
                        {['divergente', 'pendente', 'rejeitada'].includes(d.status) && (
                          <Button
                            variant="contained" size="small"
                            startIcon={<BuildIcon />}
                            onClick={() => abrirAcao('resolver', d)}
                            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
                          >
                            Resolver
                          </Button>
                        )}
                        {d.status === 'resolvida' && (
                          <>
                            <Button
                              variant="contained" size="small"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => abrirAcao('aprovar', d)}
                              sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
                            >
                              Aprovar
                            </Button>
                            <Button
                              variant="outlined" size="small" color="warning"
                              startIcon={<CancelIcon />}
                              onClick={() => abrirAcao('rejeitar', d)}
                            >
                              Rejeitar
                            </Button>
                          </>
                        )}
                        <Button
                          variant="contained" size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => abrirDetalhe(d)}
                          sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
                        >
                          Detalhes
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Paper>

      {/* Modal detalhe */}
      <Dialog open={detalheAberto} onClose={fecharDetalhe} maxWidth="lg" fullWidth>
        {/* Header vermelho */}
        <DialogTitle sx={{
          bgcolor: '#dc2626', color: 'white', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', py: 1.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <WarningIcon sx={{ fontSize: 28 }} />
            <Typography variant="h6" fontWeight="bold">
              Resolver Divergência — <Box component="span" sx={{ fontFamily: 'monospace' }}>#{divSelecionada?.id}</Box>
            </Typography>
          </Box>
          <IconButton onClick={fecharDetalhe} sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2, bgcolor: '#f8fafc' }}>
          {carregandoDetalhe ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
          ) : divSelecionada && (
            <Grid container spacing={2}>
              {/* ====== COLUNA ESQUERDA ====== */}
              <Grid item xs={12} md={7}>
                {/* Divergência Identificada */}
                <Paper sx={{
                  p: 2, mb: 2, borderRadius: 2, bgcolor: '#fef2f2',
                  borderLeft: '4px solid #dc2626'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <WarningIcon sx={{ color: '#dc2626', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#991b1b' }}>
                      Divergência Identificada
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#7f1d1d', whiteSpace: 'pre-wrap' }}>
                    {divSelecionada.descricao_divergencia || '—'}
                  </Typography>
                </Paper>

                {/* Info list */}
                <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                  <Stack spacing={1.2}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Requisição:</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                        {divSelecionada.numero_requisicao}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Prioridade:</Typography>
                      <Typography variant="body2" fontWeight="bold">{divSelecionada.prioridade}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Tipo:</Typography>
                      <Typography variant="body2" fontWeight="bold">{divSelecionada.tipo_divergencia || '—'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Status:</Typography>
                      <Box>{renderChip(STATUS_CFG[divSelecionada.status] || { label: divSelecionada.status, bg: '#e5e7eb', fg: '#374151' })}</Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Criada em:</Typography>
                      <Typography variant="body2" fontWeight="bold">{formatarData(divSelecionada.created_at)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Criada por:</Typography>
                      <Typography variant="body2" fontWeight="bold">{divSelecionada.criado_por_usuario || '—'}</Typography>
                    </Box>
                  </Stack>
                </Paper>

                {/* Imagem da Receita */}
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <ImageIcon sx={{ color: '#64748b', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight="bold">Imagem da Receita</Typography>
                  </Box>
                  {imagensReceita.length === 0 ? (
                    <Box sx={{ bgcolor: 'grey.100', p: 4, borderRadius: 1, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Nenhuma imagem</Typography>
                    </Box>
                  ) : (
                    <Box sx={{
                      bgcolor: 'grey.100', borderRadius: 1, height: 380,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      position: 'relative', overflow: 'hidden'
                    }}>
                      <Box
                        component="img" src={urlAtual}
                        alt={imgAtual?.nome_original}
                        sx={{
                          maxWidth: '100%', maxHeight: '100%',
                          transform: `scale(${imgScale}) rotate(${imgRotation}deg)`,
                          transition: 'transform 0.2s', userSelect: 'none'
                        }}
                      />
                      <Box sx={{
                        position: 'absolute', top: 8, right: 8,
                        display: 'flex', gap: 0.5,
                        bgcolor: 'rgba(0,0,0,0.55)', borderRadius: 1, p: 0.25
                      }}>
                        {[
                          { icon: <ZoomInIcon fontSize="small" />, onClick: () => setImgScale(s => Math.min(s + 0.25, 4)) },
                          { icon: <ZoomOutIcon fontSize="small" />, onClick: () => setImgScale(s => Math.max(s - 0.25, 0.25)) },
                          { icon: <RotateLeftIcon fontSize="small" />, onClick: () => setImgRotation(r => r - 90) },
                          { icon: <RotateRightIcon fontSize="small" />, onClick: () => setImgRotation(r => r + 90) },
                          { icon: <ZoomResetIcon fontSize="small" />, onClick: () => { setImgScale(1); setImgRotation(0); } },
                          { icon: <FullscreenIcon fontSize="small" />, onClick: () => window.open(urlAtual, '_blank') }
                        ].map((b, i) => (
                          <IconButton key={i} size="small" onClick={b.onClick} sx={{ color: 'white' }}>
                            {b.icon}
                          </IconButton>
                        ))}
                      </Box>
                      {imagensReceita.length > 1 && (
                        <>
                          <IconButton onClick={anteriorImg} sx={{
                            position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                            bgcolor: 'rgba(0,0,0,0.55)', color: 'white',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                          }}>◀</IconButton>
                          <IconButton onClick={proximaImg} sx={{
                            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                            bgcolor: 'rgba(0,0,0,0.55)', color: 'white',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                          }}>▶</IconButton>
                          <Chip size="small"
                            label={`${imgIdx + 1} / ${imagensReceita.length}`}
                            sx={{
                              position: 'absolute', bottom: 8, left: '50%',
                              transform: 'translateX(-50%)',
                              bgcolor: 'rgba(0,0,0,0.55)', color: 'white'
                            }}
                          />
                        </>
                      )}
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* ====== COLUNA DIREITA ====== */}
              <Grid item xs={12} md={5}>
                {/* Histórico de Divergências */}
                <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', mb: histOpen ? 1.5 : 0 }}
                    onClick={() => setHistOpen(o => !o)}
                  >
                    <HistoryIcon sx={{ color: '#64748b', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight="bold">
                      Histórico de Divergências ({historicoReceita.length})
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    {histOpen ? <ExpandLess /> : <ExpandMore />}
                  </Box>
                  {histOpen && (
                    <Stack spacing={1.5}>
                      {historicoReceita.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Esta é a primeira divergência desta receita.
                        </Typography>
                      ) : historicoReceita.map((d) => {
                        const statusCfg = STATUS_CFG[d.status] || { label: d.status, bg: '#e5e7eb', fg: '#374151' };
                        const atual = d.id === divSelecionada.id;
                        return (
                          <Paper key={d.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                              <Chip size="small"
                                label={statusCfg.label.toUpperCase()}
                                sx={{ bgcolor: statusCfg.bg, color: statusCfg.fg, fontWeight: 'bold', height: 22 }}
                              />
                              {atual && (
                                <Chip size="small" icon={<StarIcon sx={{ fontSize: 14 }} />} label="ATUAL"
                                  sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 'bold', height: 22 }} />
                              )}
                              <Box sx={{ flex: 1 }} />
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                                #{d.id}
                              </Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Descrição:</strong> {d.descricao_divergencia || '—'}
                            </Typography>
                            <Stack spacing={0.5} sx={{ fontSize: '0.85rem' }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                <WarningIcon sx={{ fontSize: 14, color: '#dc2626', mt: 0.3 }} />
                                <Typography variant="caption">
                                  Aberto por <strong>{d.criado_por_usuario || '—'}</strong> em {formatarData(d.created_at)}
                                </Typography>
                              </Box>
                              {d.resolved_at && (
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                  <EditIcon sx={{ fontSize: 14, color: '#3b82f6', mt: 0.3 }} />
                                  <Box>
                                    <Typography variant="caption">
                                      Resolvido por <strong>{d.resolvido_por_usuario || '—'}</strong> em {formatarData(d.resolved_at)}
                                    </Typography>
                                    {d.comentario_resolucao && (
                                      <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', color: 'text.secondary', ml: 0, mt: 0.25 }}>
                                        {d.comentario_resolucao}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              )}
                              {d.approved_at && (
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                  <CheckOutlineIcon sx={{ fontSize: 14, color: d.status === 'aprovada' ? '#16a34a' : '#f59e0b', mt: 0.3 }} />
                                  <Box>
                                    <Typography variant="caption">
                                      {d.status === 'aprovada' ? 'Aprovado' : 'Rejeitado'} por <strong>{d.aprovado_por_usuario || '—'}</strong> em {formatarData(d.approved_at)}
                                    </Typography>
                                    {d.comentario_aprovacao && (
                                      <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', color: 'text.secondary', ml: 0, mt: 0.25 }}>
                                        {d.comentario_aprovacao}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              )}
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}
                </Paper>

                {/* Nova Mensagem / Resolução */}
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <EditIcon sx={{ color: '#64748b', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight="bold">Nova Mensagem / Resolução</Typography>
                  </Box>

                  {error && (
                    <Alert severity="error" onClose={() => setError('')} sx={{ mb: 1.5 }}>
                      {error}
                    </Alert>
                  )}

                  <TextField
                    fullWidth multiline rows={4}
                    label={divSelecionada.status === 'resolvida' ? 'Comentário de aprovação/rejeição' : 'Comentário de Resolução *'}
                    placeholder="Descreva detalhadamente como a divergência foi resolvida..."
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                    helperText="Mínimo 10 caracteres"
                    sx={{ mb: 1.5 }}
                  />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: 'text.secondary' }}>
                    <AttachFileIcon fontSize="small" />
                    <Typography variant="caption">
                      Anexar imagem (opcional) — <em>em breve</em>
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1}>
                    <Button
                      fullWidth variant="outlined" color="inherit"
                      onClick={fecharDetalhe} disabled={salvando}
                      startIcon={<CloseIcon />}
                    >
                      Cancelar
                    </Button>

                    {['divergente', 'pendente', 'rejeitada'].includes(divSelecionada.status) && (
                      <Button
                        fullWidth variant="contained"
                        onClick={resolverInline}
                        disabled={salvando || comentario.trim().length < 10}
                        startIcon={<CheckCircleIcon />}
                        sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
                      >
                        {salvando ? 'Salvando...' : 'Resolver'}
                      </Button>
                    )}

                    {divSelecionada.status === 'resolvida' && (
                      <>
                        <Button
                          fullWidth variant="outlined" color="warning"
                          onClick={rejeitarInline}
                          disabled={salvando || comentario.trim().length < 10}
                          startIcon={<CancelIcon />}
                        >
                          Rejeitar
                        </Button>
                        <Button
                          fullWidth variant="contained" color="success"
                          onClick={aprovarInline}
                          disabled={salvando}
                          startIcon={<CheckCircleIcon />}
                        >
                          Aprovar
                        </Button>
                      </>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de ação */}
      <Dialog open={acaoModal !== null && acaoModal !== 'criar'} onClose={fecharAcao} maxWidth="sm" fullWidth>
        <DialogTitle>
          {acaoModal === 'resolver' && 'Resolver Divergência'}
          {acaoModal === 'aprovar'  && 'Aprovar Divergência'}
          {acaoModal === 'rejeitar' && 'Rejeitar Divergência'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label={acaoModal === 'aprovar' ? 'Comentário (opcional)' : 'Comentário *'}
              multiline rows={4} fullWidth
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              helperText={acaoModal !== 'aprovar' ? 'Mínimo 10 caracteres' : 'Motivo da aprovação'}
            />
            {acaoModal === 'resolver' && (
              <TextField
                label="Valor corrigido (opcional)" fullWidth
                value={valorCorrigido}
                onChange={(e) => setValorCorrigido(e.target.value)}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharAcao} disabled={salvando}>Cancelar</Button>
          <Button
            variant="contained"
            color={acaoModal === 'rejeitar' ? 'warning' : acaoModal === 'aprovar' ? 'success' : 'info'}
            onClick={executarAcao} disabled={salvando}
          >
            {salvando ? 'Salvando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal criar */}
      <Dialog open={acaoModal === 'criar'} onClose={fecharAcao} maxWidth="sm" fullWidth>
        <DialogTitle>Nova Divergência</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Número da requisição (NRRQU) *" fullWidth
              value={novaDivergencia.numero_requisicao}
              onChange={(e) => setNovaDivergencia(p => ({ ...p, numero_requisicao: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Filial *</InputLabel>
              <Select
                label="Filial *"
                value={novaDivergencia.filial_id}
                onChange={(e) => setNovaDivergencia(p => ({ ...p, filial_id: e.target.value }))}
              >
                {filiais.map(f => (
                  <MenuItem key={f.id} value={f.codigo ?? f.id}>{f.nome_filial}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Tipo de divergência" fullWidth
              placeholder="Ex: Quantidade divergente, Item faltando..."
              value={novaDivergencia.tipo_divergencia}
              onChange={(e) => setNovaDivergencia(p => ({ ...p, tipo_divergencia: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Prioridade</InputLabel>
              <Select
                label="Prioridade"
                value={novaDivergencia.prioridade}
                onChange={(e) => setNovaDivergencia(p => ({ ...p, prioridade: e.target.value }))}
              >
                {Object.entries(PRIORIDADE_CFG).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Valor original (opcional)" fullWidth
              value={novaDivergencia.valor_original}
              onChange={(e) => setNovaDivergencia(p => ({ ...p, valor_original: e.target.value }))}
            />
            <TextField
              label="Descrição *" multiline rows={4} fullWidth
              value={novaDivergencia.descricao_divergencia}
              onChange={(e) => setNovaDivergencia(p => ({ ...p, descricao_divergencia: e.target.value }))}
              helperText="Mínimo 10 caracteres"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharAcao} disabled={salvando}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={criarDivergencia} disabled={salvando}>
            {salvando ? 'Salvando...' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DivergenciasFarma;
